import React, { useState, useContext, useEffect } from 'react';
import Row from '../../common/Row';
import Icon from '../../common/Icon';
import { FormContext } from '../../../hooks/Contexts';
import {
  deleteFormMetadata,
  updateFormMetadata,
} from './handleFormMetaEntries';
import { notify } from '../../../helpers/notifications';
import Errors from '../../common/Errors';

const FormMetaEntry = ({ formMetaEntry, editor, show, error }) => {
  const { setFormMetaEntries } = useContext(FormContext);
  const dirtyMetas = editor?.dirtyMetas;
  const setDirtyMetas = editor?.setDirtyMetas;
  const [editing, setEditing] = useState(false);
  const [editableEntry, setEditableEntry] = useState(formMetaEntry);
  const [errors, setErrors] = useState(null);
  const isDirty =
    formMetaEntry.title != editableEntry.title ||
    formMetaEntry.value != editableEntry.value;

  const handleChange = (e) => {
    if (errors && errors[e.target.name]) {
      setErrors((errs) => {
        delete errs[e.target.name];
        return errs;
      });
    }
    setEditableEntry((entry) => {
      return { ...entry, [e.target.name]: e.target.value };
    });
  };

  const handleMetaSave = async (e, property) => {
    e.preventDefault();
    if (!isDirty) return setEditing(property == 'title' ? false : editing);
    let id = formMetaEntry.id;
    let response = await updateFormMetadata(
      { [property]: editableEntry[property] },
      id
    );
    if (response.result?.ok) {
      setFormMetaEntries((entries) => {
        let updated = entries.find((me) => me.id === id);
        entries[entries.indexOf(updated)] = editableEntry;
        return [...entries];
      });
      if (property == 'title') {
        setEditing(false);
        notify.alertSuccess(
          'Notice',
          '<b>Title has been changed.</b><br/>Mailers referencing this metadata title have been updated automatically.'
        );
      }
      setErrors(null);
    } else if (response.error?.errors) {
      handleErrors(response.error.errors);
      console.error(response);
    }
  };

  const handleDeleteFormMeta = async (e) => {
    e.preventDefault();
    await deleteFormMetadata(editableEntry.id, (res) => {
      if (res.ok) {
        setFormMetaEntries((entries) =>
          entries.filter((me) => me.id != editableEntry.id)
        );

        notify.alertWarning(
          'Warning',
          '<b>Manual updates may be required!</b><br/>Visit form mailers and resolve any potential issues. If this form is linked, please visit all linked form mailer pages as well.'
        );
      } else if (result.errors) {
        handleErrors(result.errors);
        console.error(res);
      }
    });
  };

  const handleErrors = (resultErrors) => {
    Object.entries(resultErrors).forEach(([key, message]) => {
      notify.failure(
        `${key.charAt(0).toUpperCase() + key.slice(1)} ${message}`,
        { showOnlyTheLastOne: false }
      );
    });
    setErrors(resultErrors);
  };

  useEffect(() => {
    if (!show) {
      setEditing(false);
      setEditableEntry(formMetaEntry);
      setErrors(null);
    }
  }, [show]);

  useEffect(() => {
    if (editor) {
      let dirtyIds = dirtyMetas.map((entry) => entry.id);
      if (isDirty) {
        setDirtyMetas((entries) => {
          if (dirtyIds.includes(formMetaEntry.id)) {
            let updated = entries.find((me) => me.id === formMetaEntry.id);
            entries[entries.indexOf(updated)] = editableEntry;
            return [...entries];
          } else {
            return [...entries, editableEntry];
          }
        });
      } else {
        setDirtyMetas(
          dirtyMetas.filter((entry) => entry.id != formMetaEntry.id)
        );
      }
    }
  }, [editableEntry]);

  useEffect(() => {
    if (error) setErrors(error.errors);
  }, [error]);

  return (
    <>
      {editor ? (
        <div className='row'>
          {errors?.title && (
            <Errors isSpan messages={{ title: errors.title }} />
          )}
          <div className='name' style={{ display: 'flex' }}>
            {editing ? (
              <>
                <input
                  type='text'
                  id={`fme-${formMetaEntry.id}-title`}
                  value={editableEntry.title}
                  placeholder='Title (Required)'
                  name='title'
                  onChange={(e) => handleChange(e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleMetaSave(e, 'title');
                    }
                  }}
                  autoFocus
                />
                <div className='right form-meta-icons'>
                  <Icon
                    icon='xmark'
                    title='Cancel'
                    onClick={(e) => {
                      setEditableEntry(formMetaEntry);
                      setErrors(null);
                      setEditing(false);
                    }}
                  />
                  <Icon
                    icon='save'
                    title='Save'
                    onClick={(e) => handleMetaSave(e, 'title')}
                  />
                </div>
              </>
            ) : (
              <>
                <div style={{ width: '82%' }}>{formMetaEntry.title}</div>
                <div className='right form-meta-icons'>
                  <Icon
                    icon='pencil'
                    onClick={() => setEditing(true)}
                    className='edit'
                  />
                  <Icon
                    icon='trash'
                    onClick={handleDeleteFormMeta}
                    className='destroy'
                  />
                </div>
              </>
            )}
          </div>
          <div className='value' style={{ marginTop: '2px' }}>
            {errors?.value && (
              <Errors isSpan messages={{ value: errors.value }} />
            )}
            <textarea
              rows={3}
              id={`fme-${formMetaEntry.id}-value`}
              value={editableEntry.value}
              placeholder='Value (500 char max, Required)'
              name='value'
              onChange={(e) => handleChange(e)}
              maxLength={500}
            />
          </div>
        </div>
      ) : (
        <Row name={formMetaEntry.title} value={formMetaEntry.value} />
      )}
    </>
  );
};

export default FormMetaEntry;
